/** @jsxImportSource @emotion/react */
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Modal } from "antd";
import { RegisterFormType } from "api/auth/type";
import InputCustom from "components/base/InputCustom";
import InputPassword from "components/base/InputPassword";
import InputPhoneNumber from "components/base/InputPhoneNumber";
import { REGEX_PASSWORD, REGEX_USERNAME } from "contants/regex";
import { ResponseCode } from "contants/response";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { selectIsLoading } from "store/auth";
import { registerMemberDetail } from "store/auth/auth.thunk";
import { setOpenModal } from "store/modal";
import { outletContextType } from "types";
import { ApiResponse } from "types/api";
import { ModalTypeValue } from "types/modal";
import * as yup from "yup";

import WrapperForm from "../WrapperForm";
import { RegisterModalStyle } from "./index.style";

const schema = yup
  .object({
    username: yup
      .string()
      .trim()
      .matches(REGEX_USERNAME, "Includes lowercase and 6 to 16 characters")
      .required("Includes alphanumeric, lowercase and 6 to 16 characters"),
    mobile: yup.string().trim().required("Phone number is not valid"),
    email: yup.string().trim().email().required("Email is not valid"),
    password: yup
      .string()
      .trim()
      .matches(REGEX_PASSWORD, "Includes alphanumeric and 6 to 15 characters")
      .required("Includes alphanumeric and 6 to 15 characters"),
    confirm_password: yup
      .string()
      .trim()
      .matches(REGEX_PASSWORD, "Includes alphanumeric and 6 to 15 characters")
      .required("Includes alphanumeric and 6 to 15 characters"),
  })
  .required();
interface props {
  code: string | null;
}
function RegisterModal({ code }: props) {
  const { openNotification } = useOutletContext<outletContextType>();

  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectIsLoading);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<RegisterFormType>({
    resolver: yupResolver<any>(schema),
    mode: "all",
  });

  useEffect(() => {
    if (code) {
      setValue("referral", code);
    }
  }, []);

  const onSubmit = async (data: RegisterFormType) => {
    const dataForm = {
      ...data,
      mobile: data.mobile?.replace("+", ""),
      fullname: data.username,
    };
    const res = await dispatch(registerMemberDetail(dataForm));

    const resData = res.payload as ApiResponse;

    if (resData.status !== ResponseCode.SUCCESS) {
      openNotification("error", res.payload as string);
      return;
    }

    dispatch(setOpenModal(ModalTypeValue.Close));
  };

  return (
    <Modal
      open={true}
      closable={false}
      onCancel={() => dispatch(setOpenModal(ModalTypeValue.Close))}
      css={RegisterModalStyle.modalContainer}
      footer={<></>}
      centered
    >
      <WrapperForm
        title={"Register"}
        formType={ModalTypeValue.Register}
        body={
          <div css={RegisterModalStyle.formContainer}>
            <Form
              css={RegisterModalStyle.formContainer}
              onSubmitCapture={handleSubmit(onSubmit)}
              autoComplete="off"
            >
              <div css={RegisterModalStyle.inputControl}>
                <label>Username</label>
                <InputCustom
                  disabled={isLoading}
                  placeholder={"Username"}
                  name={"username"}
                  margin_bottom="0px"
                  control={control}
                  errors={errors.username?.message}
                  validate_status={errors.username ? "error" : "success"}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>Password</label>
                <InputPassword
                  disabled={isLoading}
                  placeholder={"Password"}
                  name={"password"}
                  control={control}
                  margin_bottom="0px"
                  error={errors.password?.message}
                  validate_status={errors.password ? "error" : "success"}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>Confirm Password</label>
                <InputPassword
                  disabled={isLoading}
                  placeholder={"Confirm Password"}
                  name={"confirm_password"}
                  control={control}
                  margin_bottom="0px"
                  autoComplete="new-password"
                  error={errors.confirm_password?.message}
                  validate_status={
                    errors.confirm_password ? "error" : "success"
                  }
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>Email</label>
                <InputCustom
                  disabled={isLoading}
                  placeholder={"Email"}
                  name={"email"}
                  margin_bottom="0px"
                  control={control}
                  errors={errors.email?.message}
                  validate_status={errors.email ? "error" : "success"}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>Mobile</label>
                <InputPhoneNumber
                  disabled={isLoading}
                  name={"mobile"}
                  control={control}
                  margin_bottom="0px"
                  error={errors.mobile?.message}
                  validate_status={errors.mobile ? "error" : "success"}
                  onChange={(val) => {
                    //
                  }}
                />
              </div>
              <div css={RegisterModalStyle.inputControl}>
                <label>Referral code</label>
                <InputCustom
                  disabled={isLoading || !!code}
                  placeholder={"Referral code"}
                  name={"referral"}
                  margin_bottom="0px"
                  control={control}
                  errors={errors.referral?.message}
                  validate_status={errors.referral ? "error" : "success"}
                />
              </div>

              <Button
                disabled={!isValid}
                loading={isLoading}
                css={[
                  RegisterModalStyle.buttonSubmit,
                  isValid && RegisterModalStyle.buttonActive,
                ]}
                htmlType="submit"
              >
                {"Register"}
              </Button>
            </Form>
          </div>
        }
        footer={
          <div css={RegisterModalStyle.footer}>
            <p>
              Already have an account?{" "}
              <span
                onClick={() => dispatch(setOpenModal(ModalTypeValue.Login))}
              >
                Go to login
              </span>
            </p>
          </div>
        }
      />
    </Modal>
  );
}

export default RegisterModal;
